import { useState, useContext } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { PasswordInput } from '@/components/PasswordInput';
import { Lock, Check, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Full-screen gate shown when the logged-in user must change their password
// (e.g. a sales team member logging in with a temporary/admin-reset password).
export default function ForcePasswordChange() {
  const { user, setUser } = useContext(AuthContext);
  const [current, setCurrent] = useState('');
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);

  // Password must meet all security criteria.
  const validatePassword = (p) => {
    if ((p || '').length < 8) return 'Password must be at least 8 characters';
    if (!/[A-Z]/.test(p)) return 'Password must include at least 1 uppercase letter';
    if (!/[a-z]/.test(p)) return 'Password must include at least 1 lowercase letter';
    if (!/[0-9]/.test(p)) return 'Password must include at least 1 number';
    if (!/[^A-Za-z0-9]/.test(p)) return 'Password must include at least 1 special character (e.g. @, #, $, !, %)';
    return '';
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!current) { toast.error('Please enter your current (temporary) password'); return; }
    const pErr = validatePassword(pw);
    if (pErr) { toast.error(pErr); return; }
    if (pw !== confirm) { toast.error('Passwords do not match'); return; }
    setSaving(true);
    try {
      // Role-agnostic endpoint — works for tradie, sales, etc.
      await axios.post(`${API}/auth/change-password`, { current_password: current, new_password: pw }, { withCredentials: true });
      toast.success('Password updated — welcome!');
      setUser({ ...user, must_change_password: false });   // lift the gate
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to update password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-mlt-off-white flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-mlt-white rounded-2xl border border-mlt-border shadow-lg p-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-lg bg-mlt-orange-light flex items-center justify-center flex-shrink-0">
            <Lock className="w-5 h-5 text-mlt-orange" />
          </div>
          <h1 className="text-xl font-bold text-mlt-navy">Set a new password</h1>
        </div>
        <p className="text-sm text-mlt-text-secondary mb-6">
          For security, please choose a new password before continuing. Your temporary password will no longer work.
        </p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-mlt-text-primary mb-1">Current (temporary) password</label>
            <PasswordInput value={current} onChange={(e) => setCurrent(e.target.value)} placeholder="The password you just logged in with" required data-testid="force-current-password" />
          </div>
          <div>
            <label className="block text-sm font-medium text-mlt-text-primary mb-1">New password</label>
            <PasswordInput value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 8 characters" required data-testid="force-new-password" />
            <ul className="mt-2 space-y-1" data-testid="password-requirements">
              {[
                { ok: pw.length >= 8, label: 'At least 8 characters' },
                { ok: /[A-Z]/.test(pw), label: '1 uppercase letter' },
                { ok: /[a-z]/.test(pw), label: '1 lowercase letter' },
                { ok: /[0-9]/.test(pw), label: '1 number' },
                { ok: /[^A-Za-z0-9]/.test(pw), label: '1 special character (@, #, $, !, %)' },
              ].map((r) => (
                <li key={r.label} className={`flex items-center gap-1.5 text-xs ${r.ok ? 'text-mlt-success' : 'text-mlt-text-muted'}`}>
                  {r.ok ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                  {r.label}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <label className="block text-sm font-medium text-mlt-text-primary mb-1">Confirm new password</label>
            <PasswordInput value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter new password" required data-testid="force-confirm-password" />
          </div>
          <Button type="submit" disabled={saving} className="w-full bg-mlt-orange hover:bg-mlt-orange-hover text-white" data-testid="force-submit">
            {saving ? 'Saving…' : 'Set password & continue'}
          </Button>
        </form>
      </div>
    </div>
  );
}
